import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { PAGE_VIEW_ROLES, ROLE_NO, type RoleCode } from '../seed/platform.seed'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, nextDocNo, parse, requireRole, roleCodesOf, txLog } from './c07.shared'
import { dashboard } from './inventoryDashboard'
import { fgLabelDetail, fgReprint } from './inventoryFgLabel'
import { batchOfQr, stockCard, stockFg, stockRm } from './inventoryStock'
import { invScopeOf } from './invScope'
import { notify, wmsRoute } from './notification.shared'

/**
 * Endpoint custom C16 Inventory & Reports — `/api/v1/wms/inventory-reports/*` (tanpa Workflow Studio):
 * - INV-01 baca: `stock-rm`, `stock-card`, `stock-fg`, `ng-queue`.
 * - INV-01 tab Barang Not Good tulis: `ng-report` (Admin / Operator / Supervisor — operator lewat DSR-05), `ng-decide` (Supervisor), `ng-print-stickers`
 *   dan `ng-assign-move` (Admin) — memakai antrean `rm_ng_item` yang sama dengan Pendataan Awal (C15).
 * - INV-02 label FG: `fg-label` (baca detail label) dan `fg-reprint` (cetak ulang stiker, Admin / Supervisor WH FG) — `inventoryFgLabel.ts`.
 * - INV-03 baca: `dashboard` (cakupan RM atau FG menurut peran).
 * INV-02 memakai Directus standar `/items/tx_log`.
 */

const ROLE = { admin: 'admin_wh_rm', operator: 'operator_wh_rm', supervisor: 'wh_rm_supervisor' } as const
const num = (value: unknown) => Number(value ?? 0)
const iso = () => new Date().toISOString()
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
const materialOf = (id: unknown) => findRow('mt_material', idOf(id))

type NgLog = { at: string; ev: string; by: string | null }
const logOf = (item: LocalRow) => ((item.log as NgLog[] | undefined) ?? []).slice()
/** Status baris NG yang fisiknya sudah berdiri di Area Not Good (ZNG-RM) — sebelum dibawa ke Area Pemusnahan. */
const NG_ZONE_STATUS = new Set(['MOVED', 'SCHEDULED_DESTROY', 'MOVING_TO_DESTROY'])
const stamp = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 16).replace('T', ' ')

/** Baris antrean Barang Not Good (laporan ditolak disembunyikan, BA 12F). */
function ngRow(item: LocalRow) {
  const material = materialOf(item.material_id)
  const unit = findRow('rm_unit', idOf(item.unit_id))
  const reasonIds = (item.reason_ids as string[] | undefined) ?? (item.reason_id ? [String(item.reason_id)] : [])
  const bin = findRow('mt_rack_bin', idOf(item.rack_bin_id ?? unit?.rack_bin_id))
  const perBig = Math.max(1, num(unit?.small_per_big_snapshot) || num(material?.default_purchase_unit_qty) || 1)
  return {
    id: String(item.id),
    number: String(item.ngz_no ?? item.finding_no ?? ''),
    finding_no: (item.finding_no as string | null) ?? null,
    created_at: String(item.date_created),
    source: String(item.source),
    source_ref: String(item.source_ref ?? ''),
    material_id: idOf(item.material_id),
    material_code: String(material?.code ?? ''),
    material_name: String(material?.name ?? ''),
    batch_no: String(item.batch_no ?? ''),
    qr_code: (item.qr_code as string | null) ?? (unit?.qr_code as string | null) ?? null,
    sticker_ngq: (item.sticker_ngq as string | null) ?? null,
    qty: num(item.qty),
    unit_name: String(item.unit_name ?? findRow('mt_unit', idOf(material?.default_purchase_unit_id))?.name ?? ''),
    reasons: reasonIds.map(id => String(findRow('mt_reject_type', id)?.name ?? '')).filter(Boolean),
    location: NG_ZONE_STATUS.has(String(item.item_status))
      ? 'ZNG-RM'
      : item.item_status === 'AT_DESTRUCTION'
        ? 'ZMS-RM'
        : String(bin?.code ?? '') || null,
    status: String(item.item_status),
    note: (item.note as string | null) ?? null,
    stack_qty: unit ? Math.round((num(unit.qty_small) / perBig) * 100) / 100 : null,
    stack_unit: unit ? String(unit.unit_big_snapshot ?? '') : null,
    reporter: userName(item.reported_by),
    approver: userName(item.approved_by),
    move_operator: userName(item.move_operator_id),
    move_task_no: (item.move_task_no as string | null) ?? null,
    scheduled_destroy: Boolean(item.destruction_id),
    log: logOf(item).map(entry => ({ at: entry.at, ev: entry.ev, by: entry.by ? userName(entry.by) : '' }))
  }
}

export function ngQueue() {
  const items = readTable('rm_ng_item').filter(item => item.item_status !== 'REJECTED')
  const rows = items.map(ngRow).sort((a, b) => b.created_at.localeCompare(a.created_at))
  const by = (...status: string[]) => rows.filter(row => status.includes(row.status))
  const qty = (list: typeof rows) => list.reduce((sum, row) => sum + row.qty, 0)
  return {
    rows,
    counters: {
      pending_approval: by('PENDING_APPROVAL').length,
      awaiting_label: by('AWAITING_LABEL').length,
      awaiting_move: by('AWAITING_MOVE', 'MOVING').length,
      awaiting_move_qty: qty(by('AWAITING_MOVE', 'MOVING')),
      in_zone: rows.filter(row => row.status === 'MOVED' && !row.scheduled_destroy).length,
      in_zone_qty: qty(rows.filter(row => row.status === 'MOVED' && !row.scheduled_destroy)),
      scheduled: rows.filter(row => row.scheduled_destroy).length
    }
  }
}

const pick = (ids: unknown, status: string) => {
  const wanted = Array.isArray(ids) && ids.length ? new Set((ids as unknown[]).map(String)) : null
  return readTable('rm_ng_item').filter(item => item.item_status === status && (!wanted || wanted.has(String(item.id))))
}

export function ngReport(body: { unit_id?: string; qty?: number; reason_ids?: string[]; note?: string }) {
  requireRole(
    'Laporan temuan NG hanya oleh Admin, Operator atau Supervisor WH RM.',
    ROLE.admin,
    ROLE.operator,
    ROLE.supervisor
  )
  const unit = findRow('rm_unit', idOf(body.unit_id))
  if (!unit) fail('Pilih tumpukan yang ditemukan rusak.')
  if (unit?.unit_status !== 'IN_STOCK') fail('Hanya tumpukan berstatus In Stock yang bisa dilaporkan.')
  const material = materialOf(unit?.material_id)
  const perBig = Math.max(1, num(unit?.small_per_big_snapshot) || num(material?.default_purchase_unit_qty) || 1)
  const stack = num(unit?.qty_small) / perBig
  const qty = num(body.qty)
  if (!(qty > 0) || qty > stack) fail(`Jumlah NG harus antara 1 dan ${stack}.`, 'ERR_VALIDATION_FAILED')
  const reasons = (body.reason_ids ?? []).filter(Boolean)
  if (!reasons.length) fail('Pilih minimal satu alasan.')
  const findingNo = nextDocNo('TMN', 'rm_ng_item', 'finding_no')
  const unitName = String(unit?.unit_big_snapshot ?? '')
  const reasonText = reasons.map(id => String(findRow('mt_reject_type', id)?.name ?? '')).join(', ')
  const row = insertRow('rm_ng_item', {
    ...auditFieldsForCreate(),
    id: newId(),
    ngz_no: null,
    finding_no: findingNo,
    source: 'TEMUAN',
    source_ref: findingNo,
    unit_id: unit?.id,
    material_id: unit?.material_id,
    qty,
    unit_name: unitName,
    reason_id: reasons[0],
    reason_ids: reasons,
    sticker_ngq: null,
    item_status: 'PENDING_APPROVAL',
    rack_bin_id: unit?.rack_bin_id ?? null,
    warehouse_zone_id: unit?.warehouse_zone_id ?? null,
    qr_code: unit?.qr_code,
    batch_no: batchOfQr(unit?.qr_code),
    note: body.note?.trim() || null,
    reported_by: currentUserId(),
    approved_by: null,
    approved_at: null,
    destruction_id: null,
    move_operator_id: null,
    move_task_no: null,
    log: [
      {
        at: stamp(),
        ev: `Dilaporkan ${userName(currentUserId())} — ${qty} ${unitName} ${reasonText}`,
        by: currentUserId()
      },
      { at: stamp(), ev: 'Dikirim ke Supervisor WH RM untuk persetujuan', by: null }
    ]
  })
  txLog(
    'RM NG Finding',
    'INV-01',
    'ng-report',
    findingNo,
    `${qty} ${unitName} ${material?.code} dilaporkan: ${reasonText}`
  )
  return { id: row.id, finding_no: findingNo }
}

export function ngDecide(body: { id?: string; decision?: 'APPROVE' | 'REJECT' }) {
  requireRole('Persetujuan laporan temuan hanya oleh Supervisor WH RM.', ROLE.supervisor)
  const item = readTable('rm_ng_item').find(row => row.id === body.id)
  if (!item) fail('Laporan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (item?.item_status !== 'PENDING_APPROVAL')
    fail('Baris ini sudah berubah — muat ulang layarnya.', 'ERR_STATE_CONFLICT', 409)
  const at = stamp()
  if (body.decision === 'APPROVE') {
    const ngz = nextDocNo('NGZ', 'rm_ng_item', 'ngz_no')
    updateRow('rm_ng_item', String(item?.id), {
      ngz_no: ngz,
      item_status: 'AWAITING_LABEL',
      approved_by: currentUserId(),
      approved_at: iso(),
      log: [
        ...logOf(item as LocalRow),
        { at, ev: `Disetujui Supervisor WH RM — terbit ${ngz} di Area Not Good`, by: currentUserId() }
      ],
      ...auditFieldsForUpdate()
    })
    txLog('RM NG Finding', 'INV-01', 'ng-approve', String(item?.finding_no), `Laporan disetujui, terbit ${ngz}`)
    return { id: item?.id, status: 'AWAITING_LABEL', ngz_no: ngz }
  }
  if (body.decision !== 'REJECT') fail('Keputusan tidak dikenali.')
  updateRow('rm_ng_item', String(item?.id), {
    item_status: 'REJECTED',
    approved_by: currentUserId(),
    approved_at: iso(),
    log: [...logOf(item as LocalRow), { at, ev: 'Ditolak Supervisor WH RM', by: currentUserId() }],
    ...auditFieldsForUpdate()
  })
  txLog('RM NG Finding', 'INV-01', 'ng-reject', String(item?.finding_no), 'Laporan ditolak Supervisor')
  return { id: item?.id, status: 'REJECTED' }
}

export function ngPrintStickers(body: { ids?: string[]; sheets?: Record<string, number> }) {
  requireRole('Cetak stiker NG hanya oleh Admin WH RM.', ROLE.admin)
  const items = pick(body.ids, 'AWAITING_LABEL')
  if (!items.length) fail('Belum ada barang Not Good yang menunggu stiker.')
  const stickers = items.map(item => {
    const sticker = nextDocNo('NGQ', 'rm_ng_item', 'sticker_ngq')
    const sheets = Math.max(1, num(body.sheets?.[String(item.id)] ?? 1))
    updateRow('rm_ng_item', String(item.id), {
      sticker_ngq: sticker,
      sticker_sheets: sheets,
      item_status: 'AWAITING_MOVE',
      log: [
        ...logOf(item),
        { at: stamp(), ev: `Stiker Not Good ${sticker} dicetak — barang masuk antrean pemindahan`, by: currentUserId() }
      ],
      ...auditFieldsForUpdate()
    })
    return { id: String(item.id), sticker_ngq: sticker, sheets }
  })
  txLog('RM NG', 'INV-01', 'ng-stiker', stickers[0].sticker_ngq, `${stickers.length} stiker NG dicetak`)
  return { printed: stickers.length, stickers }
}

export function ngAssignMove(body: { operator_id?: string; ids?: string[] }) {
  requireRole('Penugasan pemindahan NG hanya oleh Admin WH RM.', ROLE.admin)
  const items = pick(body.ids, 'AWAITING_MOVE')
  if (!items.length) fail('Belum ada barang Not Good yang siap dipindahkan.')
  const operator = String(body.operator_id ?? '')
  const isOperator = roleCodesOf(operator).includes(ROLE.operator)
  if (!isOperator) fail('Pilih petugas Operator WH RM.')
  const taskNo = nextDocNo('TNG', 'rm_ng_item', 'move_task_no')
  items.forEach(item =>
    updateRow('rm_ng_item', String(item.id), {
      item_status: 'MOVING',
      move_operator_id: operator,
      move_task_no: taskNo,
      log: [
        ...logOf(item),
        { at: stamp(), ev: `Ditugaskan ke ${userName(operator)} (${taskNo}) — pindah ke ZNG-RM`, by: currentUserId() }
      ],
      ...auditFieldsForUpdate()
    })
  )
  txLog('RM NG', 'INV-01', 'ng-tugas', taskNo, `${items.length} baris NG ditugaskan ke ${userName(operator)}`)
  notify({
    to: { userIds: [operator] },
    type: 'MOVE_TASK_ASSIGNED',
    title: `Tugas pemindahan NG ${taskNo}`,
    body: `${userName(currentUserId())} menugaskan ${items.length} baris barang Not Good untuk dipindahkan ke ZNG-RM — buka Tugas Pemindahan NG.`,
    link: wmsRoute('ng-handling-rm/tugas-pemindahan-ng'),
    ref: { type: 'rm_ng_item', id: String(items[0].id) },
    dedupeKey: `MOVE_TASK_ASSIGNED:${taskNo}`
  })
  return { task_no: taskNo, items: items.length }
}

/** Cakupan role aktif untuk INV-01; dunia yang tidak dimiliki role ditolak (403). */
function inScope(world: 'rm' | 'fg' | 'ng') {
  const scope = invScopeOf(roleCodesOf(currentUserId()))
  if (!scope[world]) fail('Data ini di luar cakupan peran Anda.', 'ERR_UNAUTHORIZED_ROLE', 403)
  return scope
}

/** INV-03 hanya untuk peran yang boleh melihat halamannya (PAGE_VIEW_ROLES) — Sales / Marketing tidak (36 #47). */
export function dashboardFor() {
  const roles = roleCodesOf(currentUserId())
  const viewers = PAGE_VIEW_ROLES['INV-03'] ?? []
  if (!roles.some(code => viewers.includes(ROLE_NO[code as RoleCode])))
    fail('Dashboard Operasional tidak tersedia untuk peran Anda.', 'ERR_UNAUTHORIZED_ROLE', 403)
  return dashboard(roles)
}

export function registerInventoryHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/inventory-reports'
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, unknown> | undefined)))
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  // INV-01 mengikuti cakupan role (`invScopeOf`): dunia di luar cakupan ditolak, gudang RM dibatasi.
  get('stock-rm', params => stockRm({ ...params, scope_whs: inScope('rm').warehouses }))
  get('stock-card', params => stockCard({ ...params, scope_whs: inScope('rm').warehouses }))
  get('stock-fg', params => (inScope('fg'), stockFg(params ?? {})))
  get('fg-label', fgLabelDetail)
  get('ng-queue', () => (inScope('ng'), ngQueue()))
  get('dashboard', dashboardFor)
  post('fg-reprint', fgReprint)
  post('ng-report', ngReport)
  post('ng-decide', ngDecide)
  post('ng-print-stickers', ngPrintStickers)
  post('ng-assign-move', ngAssignMove)
}
